/* LiveArena — Battle Arena 3.0: real-time tap combat.
 *
 * The hidden `battle_engine_v2` remains the source of truth. The full battle
 * (rounds, logs, hp deltas, telemetry) is pre-computed on the server. This
 * component re-paces the pre-computed events using tap cadence so play FEELS
 * real-time, but the outcome is fixed. Fast tapping affects pacing, VFX and
 * ultimate charge — never the winner.
 */
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import { getRandomBattlefield, preloadSelected } from '../lib/BattlefieldManager';
import {
  Zap, Sparkles, FlaskConical, Wind, Snowflake, CloudRain,
  Cloud, Sun, Moon, Trees, Volume2, VolumeX, Settings2, X, ArrowLeft,
  Flame, Award, Bird, Rabbit, Fish, Bug, Turtle, Rat, Leaf, Sprout,
} from 'lucide-react';
import BattleInstructions from '../components/arena3d/BattleInstructions';
import { VsBanner, BattleSigil, ActionDock } from '../components/battle/BattleHudV2';
import VictoryBurst from '../components/battle/VictoryBurst';
import ShareVictoryCard from '../components/battle/ShareVictoryCard';
import { SkeletonArena } from '../components/Skeleton';
import { toast } from 'sonner';
import { useT } from '../i18n/I18nContext';
import {
  TAP_COST_ATTACK, TAP_COST_RESULT, ULTIMATE_TAP_THRESHOLD,
  CONSUMABLE_FREEZE_MS, BATTLE_TIMER_SECONDS, COMBO_TIERS,
  PERFECT_COOLDOWN_MS, PERFECT_WINDOW_START_MS, PERFECT_WINDOW_END_MS,
  buildEventQueue, classify, INITIAL, reducer,
} from '../lib/liveArenaEngine';
import { getRoundSeconds } from '../lib/roundLength';
import HabitatAffinityBadge from '../components/battle/HabitatAffinityBadge';
import EcologyIntroCard from '../components/battle/EcologyIntroCard';
import EcologySheet from '../components/battle/EcologySheet';

// -----------------------------------------------------------------------
//  Constants (visual only — engine constants live in liveArenaEngine.js)
// -----------------------------------------------------------------------
const WEATHER_ICONS = {
  clear: Sun, storm: Zap, fog: Cloud, rain: CloudRain, snow: Snowflake,
  sun: Sun, twilight: Moon,
};

const CATEGORY_ICONS = {
  Birds: Bird, Mammals: Rabbit, Reptiles: Turtle, Amphibians: Turtle,
  Fish: Fish, Insects: Bug, Plants: Sprout, Trees: Trees,
  Mushrooms: Leaf, Rodents: Rat,
};

function iconForCategory(cat) {
  return CATEGORY_ICONS[cat] || Trees;
}

// Consumable effect_kind → visual + tooltip metadata
const EFFECT_KIND_META = {
  heal_pct:      { color: '#4ade80', kind: 'heal',    label: 'Restores HP' },
  regen:         { color: '#22c55e', kind: 'heal',    label: 'HP regen over time' },
  stat:          { color: '#f59e0b', kind: 'buff',    label: 'Stat boost' },
  initiative:    { color: '#38bdf8', kind: 'buff',    label: 'Strikes first' },
  cleanse:       { color: '#a78bfa', kind: 'cleanse', label: 'Removes debuffs' },
  xp_multiplier: { color: '#facc15', kind: 'buff',    label: 'XP multiplier' },
  chaos:         { color: '#f472b6', kind: 'chaos',   label: 'Chaos surge' },
  crit_mult:     { color: '#fb7185', kind: 'damage',  label: 'Crit damage boost' },
};

function effectKindMeta(item) {
  const base = EFFECT_KIND_META[item?.effect_kind] || { color: '#22d3ee', kind: 'buff', label: 'Consumable effect' };
  const detail = item?.effect_stat
    ? ` +${item.effect_value || 0} ${item.effect_stat}`
    : (item?.effect_value ? ` +${item.effect_value}` : '');
  return { color: base.color, kind: base.kind, description: `${base.label}${detail}`.trim() };
}


// -----------------------------------------------------------------------
//  Component
// -----------------------------------------------------------------------
export default function LiveArena() {
  const nav = useNavigate();
  const loc = useLocation();
  const t = useT();
  const { battleId } = useParams();
  const [battle, setBattle] = useState(loc.state?.battle || null);
  const [loading, setLoading] = useState(!battle);
  const [error, setError] = useState(null);
  const [state, dispatch] = useReducer(reducer, null, () => INITIAL({ rounds: [] }));
  const [settings, setSettings] = useState(loadSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ecologyOpen, setEcologyOpen] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  // Read the player's chosen round length once, at mount — persisted via
  // the Battle-Hub Round Length toggle. Falls back to Casual (180s).
  const [roundSeconds] = useState(() => getRoundSeconds());
  const [timeLeft, setTimeLeft] = useState(roundSeconds);
  const holdRef = useRef(null);
  const autoTapRef = useRef(null);
  const comboTimeoutRef = useRef(null);
  const consumeClearRef = useRef(null);

  // Unmount cleanup — clear all pending timers so dispatches never fire after nav
  useEffect(() => {
    return () => {
      if (holdRef.current) clearInterval(holdRef.current);
      if (autoTapRef.current) clearInterval(autoTapRef.current);
      if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
      if (consumeClearRef.current) clearTimeout(consumeClearRef.current);
    };
  }, []);

  // Load battle data — either from route state or refetch
  useEffect(() => {
    if (battle) return;
    if (!battleId) { setError(t('arena.noBattleProvided', 'No battle provided')); setLoading(false); return; }
    let dead = false;
    api.get(`/battles/${battleId}`)
      .then(r => { if (!dead) { setBattle(r.data); setLoading(false); } })
      .catch(() => { if (!dead) { setError(t('arena.battleNotFound', 'Battle not found')); setLoading(false); } });
    return () => { dead = true; };
  }, [battle, battleId]);

  useEffect(() => { if (battle) dispatch({ type: 'INIT', battle }); }, [battle]);

  // Primed consumables — display them as an aura/tooltip cycle at battle start
  // so players can see which items are actually influencing this battle.
  useEffect(() => {
    if (!battle) return;
    const items = loc.state?.consumables || [];
    if (items.length === 0) return;
    let idx = 0;
    const show = () => {
      const item = items[idx];
      if (!item) return;
      const meta = effectKindMeta(item);
      setConsumedItem({ name: item.name || 'Consumable', color: meta.color, kind: meta.kind, description: `Primed · ${meta.description}` });
      window.clearTimeout(consumeClearRef.current);
      consumeClearRef.current = window.setTimeout(() => {
        setConsumedItem(null);
        idx += 1;
        if (idx < items.length) window.setTimeout(show, 250);
      }, 1800);
    };
    show();
  }, [battle, loc.state?.consumables]);

  // Battle timer
  useEffect(() => {
    if (!battle || state.finished) return;
    const iv = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(iv);
  }, [battle, state.finished]);
  useEffect(() => { if (timeLeft === 0 && !state.finished) dispatch({ type: 'FAST_FORWARD' }); }, [timeLeft, state.finished]);

  // Screen flash timeout
  useEffect(() => {
    if (state.screenFlash) {
      const t = setTimeout(() => dispatch({ type: 'CLEAR_FLASH' }), 340);
      return () => clearTimeout(t);
    }
  }, [state.screenFlash]);

  // Perfect-Tap haptic + audio pulse — fires ONCE per perfectFlashKey
  // bump so the player feels/hears their skill without every regular tap
  // getting a chirp. Respects the user's Sound + Haptic settings.
  useEffect(() => {
    if (!state.perfectFlashKey) return;
    if (settings.haptics !== false && navigator?.vibrate) {
      try { navigator.vibrate([12, 40, 24]); } catch { /* ignore */ }
    }
    if (settings.sound !== false && typeof window !== 'undefined' && window.AudioContext) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(1320, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.09);
        g.gain.setValueAtTime(0.001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
        o.connect(g).connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.24);
        setTimeout(() => ctx.close?.(), 500);
      } catch { /* ignore — best-effort SFX */ }
    }
  }, [state.perfectFlashKey, settings.haptics, settings.sound]);

  // Combo cooldown — reset if no tap in 1s
  const resetComboSoon = () => {
    if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
    comboTimeoutRef.current = setTimeout(() => dispatch({ type: 'BREAK_COMBO' }), 1100);
  };

  // Auto-tap (Tap Assist)
  useEffect(() => {
    if (!settings.tapAssist || state.finished) return;
    autoTapRef.current = setInterval(() => dispatch({ type: 'AUTO_TAP' }), 260);
    return () => clearInterval(autoTapRef.current);
  }, [settings.tapAssist, state.finished]);

  // Hold-to-tap
  const startHold = () => {
    if (!settings.holdToTap) return;
    if (holdRef.current) clearInterval(holdRef.current);
    holdRef.current = setInterval(() => { dispatch({ type: 'TAP' }); resetComboSoon(); }, 140);
  };
  const stopHold = () => {
    if (holdRef.current) { clearInterval(holdRef.current); holdRef.current = null; }
  };

  const persist = (next) => { setSettings(next); localStorage.setItem('wq_arena3_settings', JSON.stringify(next)); };

  // ---- Derived data ----
  const bp = battle?.battle_prep || {};
  const chSnap = battle?.challenger_snapshot || {};
  const opSnap = battle?.opponent_snapshot || {};
  const chName = chSnap.name || 'Challenger';
  const opName = opSnap.name || 'Opponent';
  const winner = battle?.winner === 'challenger' ? 'challenger' : 'opponent';

  const currentLogLine = state.logLines[0];
  const [consumedItem, setConsumedItem] = useState(null); // { color, name, kind }

  // ---- Floating damage numbers ----
  // Backend now ships a per-round `hits` list. We consume hits one at
  // a time as their round advances, so each pop lines up roughly with
  // the narration line it belongs to.
  const [damagePops, setDamagePops] = useState({ challenger: [], opponent: [] });
  const hitsIndexRef = useRef({ round: 0, index: 0 });
  useEffect(() => {
    if (!battle) return;
    // Detect when we cross into an attack-ish line — that's when we spawn
    // the next hit-pop for the current round.
    const line = state.logLines[0];
    if (!line) return;
    const spawnKinds = new Set(['attack', 'crit', 'counter']);
    if (!spawnKinds.has(line.kind)) return;
    const rounds = battle.rounds || [];
    // Find current round based on state.currentRound
    const roundIdx = Math.max(0, (state.currentRound || 1) - 1);
    if (roundIdx !== hitsIndexRef.current.round) {
      hitsIndexRef.current = { round: roundIdx, index: 0 };
    }
    const roundHits = rounds[roundIdx]?.hits || [];
    const nextHit = roundHits[hitsIndexRef.current.index];
    if (!nextHit) return;
    hitsIndexRef.current.index += 1;
    const pop = {
      id: `p_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      value: Math.max(1, Math.abs(nextHit.value || 0)),
      color: nextHit.kind === 'heal' ? '#4ade80'
           : nextHit.crit ? '#fbbf24'
           : nextHit.dot ? '#a78bfa'
           : nextHit.counter ? '#22d3ee'
           : '#f87171',
      crit: !!nextHit.crit,
      kind: nextHit.kind,
    };
    const side = nextHit.target_side; // 'challenger' | 'opponent'
    setDamagePops(prev => ({
      ...prev,
      [side]: [...prev[side], pop],
    }));
    // Auto-cleanup after the animation duration
    setTimeout(() => {
      setDamagePops(prev => ({
        ...prev,
        [side]: prev[side].filter(p => p.id !== pop.id),
      }));
    }, 1300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.logLines[0]?.text]);

  const chSide = { side: 'challenger', name: chName, snap: chSnap, hp: state.hp.ch,
                   statuses: state.status.ch, passives: bp.passives?.challenger || [], ui: 'emerald' };
  const opSide = { side: 'opponent', name: opName, snap: opSnap, hp: state.hp.op,
                   statuses: state.status.op, passives: bp.passives?.opponent || [], ui: 'red' };

  const cameraMode = state.lastLineKind === 'crit' ? 'zoom'
                    : state.lastLineKind === 'dodge' ? 'pan'
                    : (inSlowMoWindow(state) ? 'slowmo' : 'idle');

  // Coin reward toast — fires once at the end of a winning battle so the
  // reward moment lands during the VictoryBurst.
  const coinToastFiredRef = useRef(false);
  const [rematching, setRematching] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!state.finished || !battle) return;
    if (coinToastFiredRef.current) return;
    const ca = battle?.coins_awarded;
    if (!ca) return;
    coinToastFiredRef.current = true;
    if (ca.won && ca.earned) {
      toast.success(t('arena.coinsGained', '+{amount} WildCoins', { amount: ca.earned }), {
        description: `${t('arena.balance', 'Balance:')} ${ca.new_balance}`,
        duration: 4200,
      });
    } else if (!ca.won && ca.earned) {
      toast(t('arena.coinsGained', '+{amount} WildCoins', { amount: ca.earned }), {
        description: `${t('arena.balance', 'Balance:')} ${ca.new_balance}`,
        duration: 3200,
      });
    }
  }, [state.finished, battle]);

  // Tap-shield — when the battle finishes, briefly (700ms) block ALL
  // pointer events so a player who's still mashing the paw button
  // doesn't accidentally hit the Rematch / Exit buttons on the
  // outcome screen. Fixes the "battle ended but I'm still tapping"
  // frustration that navigated users to random pages. Kept above the
  // early-return guards below because React Hooks must run in the
  // same order every render.
  const [shieldActive, setShieldActive] = useState(false);
  // Delay the ecology intro card until the first-run BattleInstructions
  // modal (if any) has dismissed. Prevents the tutorial from painting
  // ON TOP of the ecology subtitle on brand-new players' first battle.
  const [introReady, setIntroReady] = useState(() => {
    try { return !!localStorage.getItem('wq_battle_intro_seen'); }
    catch { return true; }
  });
  useEffect(() => {
    if (introReady) return;
    // Poll for tutorial-seen flag; the BattleInstructions modal writes
    // this key on dismiss. Cheap: fires only once per new-player battle.
    const iv = setInterval(() => {
      try {
        if (localStorage.getItem('wq_battle_intro_seen')) {
          setIntroReady(true);
          clearInterval(iv);
        }
      } catch { setIntroReady(true); clearInterval(iv); }
    }, 300);
    // Failsafe — always reveal after 6s max so if the modal breaks,
    // ecology intro still surfaces at some point.
    const failsafe = setTimeout(() => { setIntroReady(true); clearInterval(iv); }, 6000);
    return () => { clearInterval(iv); clearTimeout(failsafe); };
  }, [introReady]);
  useEffect(() => {
    if (!state.finished) { setShieldActive(false); return; }
    setShieldActive(true);
    const t = setTimeout(() => setShieldActive(false), 700);
    return () => clearTimeout(t);
  }, [state.finished]);

  // -----------------------------------------------------------
  //  Render
  // -----------------------------------------------------------
  if (loading) return (
    <div className="min-h-screen" data-testid="arena3-loading">
      <SkeletonArena />
    </div>
  );
  if (error) return (
    <div className="p-10 text-center text-muted-foreground" data-testid="arena3-error">
      <p>{error}</p>
      <button onClick={() => nav('/battle')} className="mt-3 px-3 py-1.5 rounded-full bg-white/5 text-[11px] uppercase tracking-widest">{t('common.back', 'Back')}</button>
    </div>
  );

  const showEnd = state.finished;
  const inSlowMo = Date.now() < state.slowMoUntil;

  // Rematch — restart the same matchup with the same cards/arena.
  const rematch = async () => {
    if (rematching || !battle) return;
    setRematching(true);
    try {
      const body = {
        challenger_card_id: battle.challenger_card_id,
        opponent_card_id: battle.opponent_card_id,
        best_of: battle.best_of || 5,
        mode: battle.mode || 'practice',
        arena: battle.arena || bp.arena,
        weather: battle.weather || bp.weather,
      };
      const r = await api.post('/battles', body);
      const newBattle = r.data;
      const newId = newBattle?.battle_id;
      if (!newId) {
        toast.error(t('arena.rematchFailed', 'Rematch failed to start'));
        setRematching(false);
        return;
      }
      nav(`/battle/live/${newId}`, { state: { battle: newBattle }, replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || t('arena.rematchFailed', 'Rematch failed to start'));
      toast.error(msg);
      setRematching(false);
    }
  };

  return (
    <motion.div
      key={state.screenShakeKey}
      initial={false}
      animate={state.lastLineKind === 'crit'
                ? { x: [0, -6, 6, -4, 4, 0], y: [0, 2, -2, 1, -1, 0] }
                : { x: 0, y: 0 }}
      transition={{ duration: 0.32 }}
      className="relative rounded-3xl overflow-hidden border border-white/10"
      data-testid="arena3-root"
      style={{
        perspective: 800,
        // Use dynamic viewport height so mobile browser chrome doesn't
        // clip or over-extend the arena on iOS. `dvh` shrinks with the
        // address bar; `svh` is the fallback for older Safari.
        minHeight: '100svh',
        height: '100dvh',
        // Respect notch / home indicator on modern phones.
        paddingTop: 'env(safe-area-inset-top, 0)',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
      }}
    >
      {/* Living arena background (Phase C) */}
      <ArenaBackdrop arena={bp.arena} weather={bp.weather} reducedMotion={settings.reducedMotion} />

      {/* First-time battle tutorial — same overlay used by CinematicArena. */}
      <BattleInstructions />

      {/* Full-screen VFX overlays (Phase B) */}
      <VfxLayer flash={state.screenFlash} slowMo={inSlowMo} reducedMotion={settings.reducedMotion} />

      {/* HUD */}
      <div className="relative z-20 h-full flex flex-col px-2.5 pt-2 pb-1.5 sm:px-4 sm:pt-3">
        {/* Top row — VS banner with portraits + HP bars + centered VS ring +
            timer. Exit and Settings are icon-only overlays at the outer edges. */}
        <div className="flex items-center gap-1.5 h-16 shrink-0">
          <button onClick={() => nav('/battle')} data-testid="arena3-exit"
            className="h-10 w-10 shrink-0 rounded-full glass border border-white/10 flex items-center justify-center hover:bg-white/10"
            aria-label="Exit battle">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <VsBanner chSide={chSide} opSide={opSide} timeLeft={timeLeft} />
          </div>
          <button onClick={() => setSettingsOpen(true)} data-testid="arena3-settings-btn"
            className="h-10 w-10 shrink-0 rounded-full glass border border-white/10 flex items-center justify-center hover:bg-white/10"
            aria-label="Battle settings">
            <Settings2 className="w-4 h-4" />
          </button>
        </div>

        {/* Weather / round pill row — tiny secondary info, right-aligned so
            portrait HP is the visual focus. */}
        <div className="flex justify-center mt-1 shrink-0">
          <StagePillbar prep={bp} timeLeft={null} />
        </div>

        {/* Habitat Affinity strip — shows a small +% badge for each fighter
           when the auto-selected arena is their natural biome. Silent when
           neither side has an affinity bonus. */}
        {bp?.habitat_affinity && (bp.habitat_affinity.challenger_pct > 0 || bp.habitat_affinity.opponent_pct > 0) && (
          <div className="flex justify-center gap-2 mt-1 shrink-0" data-testid="habitat-affinity-row">
            <HabitatAffinityBadge
              pct={bp.habitat_affinity.challenger_pct}
              arena={bp.habitat_affinity.arena_label || bp.arena}
              compact
              testId="habitat-affinity-ch"
            />
            {bp.habitat_affinity.is_shared_habitat && (
              <span className="text-[9px] uppercase tracking-widest text-emerald-200/80 self-center" data-testid="habitat-shared">
                shared habitat
              </span>
            )}
            <HabitatAffinityBadge
              pct={bp.habitat_affinity.opponent_pct}
              arena={bp.habitat_affinity.arena_label || bp.arena}
              compact
              testId="habitat-affinity-op"
            />
          </div>
        )}

        {/* Optional 🌿 Ecology button — one-tap deep-dive into both
           species' ecology mid-battle. Never mandatory, never pauses. */}
        <div className="flex justify-center mt-1 shrink-0">
          <button
            type="button"
            onClick={() => setEcologyOpen(true)}
            data-testid="arena-ecology-btn"
            aria-label="Learn ecology of both species in this battle"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 hover:bg-emerald-500/20 text-[10px] uppercase tracking-widest text-emerald-200"
          >
            <Leaf className="w-3 h-3" strokeWidth={2.4} /> Ecology
          </button>
        </div>

        {/* Cinematic battle stage (creatures + camera + centered log + tooltip) */}
        <div className="flex-1 min-h-0 flex flex-col mt-1">
          <BattleStage
            chSide={chSide}
            opSide={opSide}
            currentLine={currentLogLine}
            cameraMode={cameraMode}
            comboCount={state.comboCounter}
            consumedItem={consumedItem}
            damagePops={damagePops}
            reducedMotion={settings.reducedMotion}
          />
        </div>

        {/* 3-button action dock — Clash-Royale-style triangular layout
            with the primary paw button centered for perfect thumb reach. */}
        <div className="mt-1.5 shrink-0" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0), 6px)' }}>
          <ActionDock
            onTap={() => { dispatch({ type: 'TAP' }); resetComboSoon(); }}
            onHoldStart={startHold}
            onHoldEnd={stopHold}
            tapEnergy={state.tapEnergy}
            tapCost={state.queue[state.cursor]?.tap_cost || 1}
            tapFrozen={Date.now() < state.frozenUntil}
            abilities={state.abilities.ch}
            onFireAbility={() => { dispatch({ type: 'FIRE_ABILITY' }); resetComboSoon(); }}
            consumables={loc.state?.consumables || []}
            ultimateCharge={state.ultimateCharge}
            ultimateFired={state.ultimateFired}
            onFireUltimate={() => dispatch({ type: 'FIRE_ULTIMATE' })}
            reducedMotion={settings.reducedMotion}
            perfectCooldownStart={state.perfectCooldownStart}
            perfectStreak={state.perfectStreak}
            perfectFlashKey={state.perfectFlashKey}
          />
        </div>
      </div>

      {/* Settings sheet */}
      <AnimatePresence>
        {settingsOpen && (
          <SettingsSheet
            settings={settings}
            onClose={() => setSettingsOpen(false)}
            onChange={persist}
            t={t}
          />
        )}
      </AnimatePresence>

      {/* Ecology intro card — 2s ecology subtitle at battle open.
         Held back until the first-run BattleInstructions modal has
         dismissed so both never fight for the same top-center slot. */}
      {!introDismissed && introReady && bp?.arena && (
        <EcologyIntroCard
          arenaKey={bp.arena}
          arenaLabel={bp?.habitat_affinity?.arena_label}
          isSharedHabitat={!!bp?.habitat_affinity?.is_shared_habitat}
          reducedMotion={settings.reducedMotion}
          onDismiss={() => setIntroDismissed(true)}
        />
      )}

      {/* Optional in-battle Ecology deep-dive sheet */}
      <EcologySheet
        open={ecologyOpen}
        onClose={() => setEcologyOpen(false)}
        chSnapshot={chSnap}
        opSnapshot={opSnap}
        arenaKey={bp?.arena}
        arenaLabel={bp?.habitat_affinity?.arena_label}
        isSharedHabitat={!!bp?.habitat_affinity?.is_shared_habitat}
      />

      {/* Post-battle Tap-Shield — swallows any lingering taps for the
         first 700ms after the battle ends so mash-happy players don't
         accidentally trigger Rematch / Exit / nav buttons. */}
      {shieldActive && (
        <div
          data-testid="post-battle-tap-shield"
          aria-hidden
          className="fixed inset-0 z-[95]"
          style={{ background: 'transparent', touchAction: 'none' }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        />
      )}

      {/* End-of-battle overlay */}
      <AnimatePresence>
        {showEnd && !shieldActive && (
          <BattleEndOverlay
            winner={winner}
            chName={chName}
            opName={opName}
            roundWins={battle?.round_wins || { challenger: 0, opponent: 0 }}
            coinsAwarded={battle?.coins_awarded}
            arena={bp.arena || battle?.arena}
            onExit={() => nav('/battle')}
            onRematch={rematch}
            rematching={rematching}
            onShare={() => setShareOpen(true)}
            t={t}
          />
        )}
      </AnimatePresence>

      {/* Victory burst — gold-coin particle explosion when the player wins.
          Sits above the end overlay so the moment lands the instant "Victory"
          appears. Only fires on challenger win. */}
      <VictoryBurst active={showEnd && winner === 'challenger'} />

      {/* Shareable Victory Card modal — canvas-rendered PNG the winner can
          download or share via the Web Share API. */}
      <ShareVictoryCard
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        data={{
          won: winner === 'challenger',
          winnerName: winner === 'challenger' ? chName : opName,
          loserName: winner === 'challenger' ? opName : chName,
          winnerPicture: (winner === 'challenger' ? chSnap : opSnap)?.picture,
          rounds: `${battle?.round_wins?.challenger ?? 0} – ${battle?.round_wins?.opponent ?? 0}`,
          arena: bp.arena || battle?.arena,
          coinsEarned: battle?.coins_awarded?.earned,
        }}
      />
    </motion.div>
  );
}


// -----------------------------------------------------------------------
//  Combatant Card
// -----------------------------------------------------------------------
function inSlowMoWindow(state) {
  return typeof state?.slowMoUntil === 'number' && Date.now() < state.slowMoUntil;
}


// -----------------------------------------------------------------------
//  Battle Stage — big creatures + camera + centered log
// -----------------------------------------------------------------------
function BattleStage({ chSide, opSide, currentLine, cameraMode, comboCount, consumedItem, damagePops, reducedMotion }) {
  const chAction = currentLine?.kind === 'crit' || currentLine?.kind === 'attack'
    ? 'attack'
    : currentLine?.kind === 'counter' ? 'counter'
    : currentLine?.kind === 'dodge' ? 'dodge'
    : currentLine?.kind === 'miss' ? 'miss'
    : 'idle';
  const chLast = currentLine?.text?.toLowerCase().includes(chSide.name.toLowerCase());
  const opActs = chLast ? true : false;

  const camAnim = reducedMotion ? { scale: 1, x: 0, y: 0 }
    : cameraMode === 'zoom' ? { scale: [1, 1.08, 1], x: [0, -4, 4, 0], y: [0, 3, -2, 0] }
    : cameraMode === 'pan'  ? { scale: 1, x: [0, 12, -12, 0], y: 0 }
    : cameraMode === 'slowmo' ? { scale: [1, 1.12, 1.06], x: 0, y: 0 }
    : { scale: 1, x: 0, y: 0 };

  return (
    <div className="relative flex-1 flex flex-col items-center justify-center" data-testid="battle-stage">
      <motion.div
        className="w-full h-full flex items-end justify-around gap-1 pb-6"
        animate={camAnim}
        transition={{ duration: cameraMode === 'slowmo' ? 0.9 : 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex-1 flex items-end justify-center">
          <Creature snap={chSide.snap} name={chSide.name} hp={chSide.hp} color="#4ade80"
            facing="right" role="ch" action={!opActs ? chAction : 'idle'}
            statuses={chSide.statuses} consumedItem={consumedItem}
            damagePops={damagePops.challenger}
            reducedMotion={reducedMotion} testid="creature-challenger" />
        </div>
        <div className="flex-1 flex items-end justify-center">
          <Creature snap={opSide.snap} name={opSide.name} hp={opSide.hp} color="#f87171"
            facing="left" role="op" action={opActs ? chAction : 'idle'}
            statuses={opSide.statuses} reducedMotion={reducedMotion}
            damagePops={damagePops.opponent}
            testid="creature-opponent" />
        </div>
      </motion.div>

      {/* Ground rune sigil beneath the fighters. */}
      <BattleSigil />

      {/* Floating log bubble — appears above the fighters, kept short so
          the visual focus stays on the combat, not the text. */}
      <div className="absolute left-1/2 -translate-x-1/2 top-2 max-w-[90%] pointer-events-none">
        <AnimatePresence mode="popLayout">
          {currentLine && currentLine.kind !== 'flavor' && (
            <motion.p
              key={currentLine.text}
              initial={{ opacity: 0, y: -6, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              data-testid="arena3-log-line"
              className={`text-center text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-md bg-black/60 border border-white/10 whitespace-nowrap overflow-hidden text-ellipsis ${
                currentLine.kind === 'crit' ? 'text-amber-300' :
                currentLine.kind === 'dodge' ? 'text-cyan-300' :
                currentLine.kind === 'combo' ? 'text-fuchsia-300 italic' :
                currentLine.kind === 'round_intro' ? 'text-[10px] uppercase tracking-[0.25em] text-accent' :
                'text-white/95'
              }`}
              style={{ maxWidth: 'min(88vw, 320px)' }}
            >{currentLine.text}</motion.p>
          )}
        </AnimatePresence>
      </div>

      {comboCount >= 5 && (
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          key={`combo-tag-${comboCount}`}
          data-testid="arena3-combo"
          className="absolute top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass border border-fuchsia-400/40 pointer-events-none"
        >
          <Flame className="w-3 h-3 text-fuchsia-300" />
          <span className="font-mono text-base text-fuchsia-100">{comboCount}</span>
          <span className="text-[9px] uppercase tracking-widest text-fuchsia-300">combo</span>
        </motion.div>
      )}

      <AnimatePresence>
        {consumedItem && <ConsumableTooltip item={consumedItem} />}
      </AnimatePresence>
    </div>
  );
}


function ConsumableTooltip({ item }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.94 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      data-testid="consumable-tooltip"
      className="absolute bottom-6 left-4 sm:left-10 z-30 pointer-events-none"
    >
      <div
        className="rounded-2xl px-3 py-2 flex items-center gap-2 backdrop-blur-md"
        style={{
          background: `linear-gradient(135deg, ${item.color}22, rgba(0,0,0,0.55))`,
          border: `1px solid ${item.color}66`,
          boxShadow: `0 0 26px -6px ${item.color}, 0 0 60px -18px ${item.color}`,
        }}
      >
        <FlaskConical className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
        <div className="flex flex-col leading-tight">
          <span className="text-xs font-semibold text-white/95">{item.name}</span>
          <span className="text-[9px] uppercase tracking-widest" style={{ color: item.color }}>{item.description}</span>
        </div>
      </div>
    </motion.div>
  );
}


function Creature({ snap, name, hp, color, facing, role, action, statuses, consumedItem, reducedMotion, testid, damagePops }) {
  const CatIcon = iconForCategory(snap?.species_category);
  const pct = Math.max(0, Math.min(1, hp || 0)) * 100;
  const flip = facing === 'left' ? -1 : 1;

  // Bigger, punchier reactions befitting a card battler. Now with
  // rotateY tilts and z-depth so the sprite feels like a 3D figure
  // rather than a flat photo.
  const bodyAnim = reducedMotion ? {} : (
      action === 'attack' ? { x: [0, flip * 46, 0], scale: [1, 1.10, 1], rotateY: [0, flip * -14, 0], rotateZ: [0, flip * 6, 0] }
    : action === 'dodge'  ? { x: [0, flip * -28, flip * 10, 0], y: [0, -10, 0], rotateY: [0, flip * 22, flip * -8, 0] }
    : action === 'miss'   ? { x: [0, flip * 12, 0], rotateY: [0, flip * 8, 0] }
    : action === 'counter'? { x: [0, flip * -14, 0], scale: [1, 1.06, 1], rotateY: [0, flip * -10, 0] }
    : { y: [0, -6, 0], scale: [1, 1.02, 1] } // idle breathing bob
  );
  const bodyDur = action === 'attack' ? 0.45 : action === 'dodge' ? 0.42 : action === 'idle' ? 3.2 : 0.36;

  const showAura = role === 'ch' && consumedItem?.color;
  const hasPicture = !!snap?.picture;
  const roleKey = role === 'ch' ? 'challenger' : 'opponent';

  return (
    <motion.div
      className="relative flex flex-col items-center"
      data-testid={testid}
      animate={{ opacity: hp <= 0 ? 0.35 : 1 }}
      style={{ perspective: 600 }}
    >
      {/* Fighter body — responsive sizing scaled up now that the name/HP
          moved to the top VS banner. Roughly 36% viewport width so both
          creatures + a small gap fit in the arena side-by-side. */}
      <motion.div
        className="relative flex items-end justify-center"
        style={{
          width: 'clamp(9rem, 36vw, 13rem)',
          aspectRatio: '3 / 4',
          transformStyle: 'preserve-3d',
        }}
        animate={bodyAnim}
        transition={{
          duration: bodyDur,
          ease: action === 'idle' ? 'easeInOut' : [0.22, 1, 0.36, 1],
          repeat: action === 'idle' ? Infinity : 0,
        }}
      >
        {/* Rarity glow behind the character */}
        <div className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 50% 45%, ${color}44 0%, ${color}18 35%, transparent 65%)`,
            filter: 'blur(6px)',
            transform: 'translateZ(-30px)',
          }}
        />

        {/* Ground disc (soft cast shadow — the character stands on this) */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '78%', height: 18,
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 45%, transparent 75%)',
            filter: 'blur(2px)',
          }}
        />

        {/* Rim-light ellipse — a bright hairline at the fighter's feet
            sold as light bouncing up from the stone circle. */}
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '62%', height: 6,
            background: `radial-gradient(ellipse at center, ${color}, transparent 60%)`,
            filter: 'blur(1px)', opacity: 0.9, mixBlendMode: 'screen',
          }}
        />
        {/* Inner hot core */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '30%', height: 3,
            background: `radial-gradient(ellipse at center, rgba(255,255,255,0.9), ${color} 40%, transparent 70%)`,
            opacity: 0.85, filter: 'blur(0.5px)', mixBlendMode: 'screen',
          }}
        />

        {/* Consumable aura */}
        <AnimatePresence>
          {showAura && (
            <motion.div
              key={consumedItem.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: [0.9, 0.4], scale: [1, 1.35] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6 }}
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${consumedItem.color}55, transparent 65%)`,
                       boxShadow: `0 0 50px ${consumedItem.color}` }}
              data-testid="consumable-aura"
            />
          )}
        </AnimatePresence>

        {/* Character illustration — with idle breathing scale for that
            "alive on the field" 2.5D feel. */}
        <motion.div
          className="relative w-full h-full flex items-end justify-center"
          animate={reducedMotion || action !== 'idle' ? {} : { scale: [1, 1.015, 1] }}
          transition={{ duration: 2.4, ease: 'easeInOut', repeat: action === 'idle' ? Infinity : 0 }}
        >
          {hasPicture ? (
            <>
              {/* Vignette mask — soft radial fade so a rectangular
                  user photo blends into the arena instead of looking
                  like a floating thumbnail. Applied via mask-image so
                  transparent Nano-Banana PNGs are unaffected in the
                  centre but still get their edges softened. When the
                  card owner has tuned a custom framing (`snap.frame`),
                  we honour their x/y/scale — otherwise fall back to
                  the sensible default (50/55/100). */}
              {(() => {
                const _fr = snap.frame || {};
                const fx = typeof _fr.x === 'number' ? _fr.x : 50;
                const fy = typeof _fr.y === 'number' ? _fr.y : 55;
                const fs = (typeof _fr.scale === 'number' ? _fr.scale : 100) / 100;
                const mask = `radial-gradient(ellipse 78% 88% at ${fx}% ${fy}%, #000 55%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0) 100%)`;
                return (
                  <>
                    <img
                      src={snap.picture}
                      alt={name}
                      data-testid="arena-fighter-photo"
                      className="relative w-full h-full object-contain object-bottom pointer-events-none"
                      style={{
                        transform: `scaleX(${flip}) scale(${fs})`,
                        transformOrigin: '50% 100%',
                        filter: `drop-shadow(0 12px 10px rgba(0,0,0,0.7)) drop-shadow(0 0 22px ${color}77) contrast(1.06) saturate(1.1)`,
                        WebkitMaskImage: mask,
                        maskImage: mask,
                      }}
                    />
                    {/* Ambient tint wash — colour of the fighter's aura
                        bleeding into the photo so it reads as "lit by the
                        arena" not a foreign thumbnail. Very low opacity
                        screen-blend. */}
                    <div className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `radial-gradient(ellipse at ${fx}% ${Math.max(0, fy - 10)}%, ${color}22 0%, transparent 60%)`,
                        mixBlendMode: 'screen',
                        opacity: 0.55,
                      }}
                    />
                  </>
                );
              })()}
              {/* Specular highlight — soft gradient over the top of
                  the sprite, gives it a sheen like a lit 3D model. */}
              <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-40"
                style={{
                  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.1) 30%, rgba(0,0,0,0.35) 100%)',
                  WebkitMaskImage: `url(${snap.picture})`,
                  maskImage: `url(${snap.picture})`,
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskPosition: 'center bottom',
                  maskPosition: 'center bottom',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  transform: `scaleX(${flip})`,
                }}
              />
              {/* Bottom fade — extra scrim so the photo settles onto
                  the ground disc instead of ending in a hard edge. */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(4,10,8,0) 0%, rgba(4,10,8,0.55) 70%, rgba(4,10,8,0.85) 100%)',
                }}
              />
            </>
          ) : (
            <div className="relative w-full h-full flex items-end justify-center pb-4"
              style={{ filter: `drop-shadow(0 10px 8px rgba(0,0,0,0.65)) drop-shadow(0 0 18px ${color}66)` }}>
              <CatIcon className="w-24 h-24 sm:w-28 sm:h-28"
                style={{ color, transform: `scaleX(${flip})` }} strokeWidth={1.3} />
            </div>
          )}
        </motion.div>

        {/* Status chip stack */}
        <div className="absolute -top-1 -right-1 flex flex-col gap-0.5 pointer-events-none">
          {(statuses || []).slice(0, 2).map((s, i) => (
            <span key={i} data-testid="arena3-status-icon"
              className="text-[8px] uppercase tracking-widest px-1 py-0.5 rounded-md bg-black/70 border border-white/15 text-white/85"
              title={s.name || s.code}>
              {(s.name || s.code || '').slice(0, 3)}
            </span>
          ))}
        </div>

        {/* Floating damage numbers — AAA card-game style. Each pop
            gets a unique id and animates upward + fades out. */}
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          <AnimatePresence>
            {(damagePops || []).map(pop => (
              <motion.div
                key={pop.id}
                data-testid={`arena3-${roleKey}-damage-pop`}
                initial={{ opacity: 0, y: 0, scale: 0.6 }}
                animate={{ opacity: [0, 1, 1, 0], y: -70, scale: [0.6, 1.15, 1.05, 0.95] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.15, times: [0, 0.2, 0.75, 1], ease: 'easeOut' }}
                className="absolute left-1/2 top-8 -translate-x-1/2 font-mono font-bold text-3xl select-none"
                style={{
                  color: pop.color,
                  textShadow: `0 2px 8px rgba(0,0,0,0.95), 0 0 12px ${pop.color}88`,
                  WebkitTextStroke: '1px rgba(0,0,0,0.6)',
                }}
              >
                {pop.crit ? '! ' : ''}{pop.kind === 'heal' ? '+' : (pop.kind === 'damage' ? '-' : '')}{pop.value}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}


// -----------------------------------------------------------------------
//  Stage pill-bar (arena/weather/season + timer)
// -----------------------------------------------------------------------
function StagePillbar({ prep, timeLeft }) {
  const WIcon = WEATHER_ICONS[prep.weather] || Sun;
  const mm = Math.floor(timeLeft / 60);
  const ss = String(timeLeft % 60).padStart(2, '0');
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {prep.arena && <Pill icon={Trees} label={prep.arena} testid="pill-arena" />}
      {prep.weather && <Pill icon={WIcon} label={prep.weather} testid="pill-weather" />}
      {prep.season && <Pill icon={Wind} label={prep.season} testid="pill-season" />}
      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full glass border border-white/10" data-testid="arena3-timer">
        <span className="font-mono text-xs tabular-nums text-white/90">{mm}:{ss}</span>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, label, testid }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full glass border border-white/10 text-[9px] uppercase tracking-widest text-white/80 capitalize" data-testid={testid}>
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
}


// -----------------------------------------------------------------------
//  Tap Zone
// -----------------------------------------------------------------------
function TapZone({ onTap, onHoldStart, onHoldEnd, frozen, energy, costHint,
                   perfectCooldownStart, perfectStreak, perfectFlashKey }) {
  const t = useT();
  const [ripple, setRipple] = useState(0);
  const pct = Math.min(100, (energy / (costHint || 1)) * 100);

  // -------- Perfect-Tap cooldown ring --------
  // We animate the ring locally via requestAnimationFrame so the reducer
  // doesn't have to fire a state update every 16ms. Reads
  // perfectCooldownStart from the reducer; the ring resets whenever
  // that timestamp changes.
  const [ringPct, setRingPct] = useState(0);
  const rafRef = useRef(0);
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    if (!perfectCooldownStart) { setRingPct(0); return; }
    const start = perfectCooldownStart;
    const loop = () => {
      const dt = Date.now() - start;
      const p = Math.min(100, (dt / PERFECT_COOLDOWN_MS) * 100);
      setRingPct(p);
      if (dt < PERFECT_COOLDOWN_MS) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [perfectCooldownStart]);

  const windowLo = (PERFECT_WINDOW_START_MS / PERFECT_COOLDOWN_MS) * 100;
  const windowHi = (PERFECT_WINDOW_END_MS / PERFECT_COOLDOWN_MS) * 100;
  const inSweetSpot = ringPct >= windowLo && ringPct <= windowHi;

  return (
    <button
      onPointerDown={() => { if (frozen) return; onTap(); setRipple(r => r + 1); onHoldStart?.(); }}
      onPointerUp={onHoldEnd}
      onPointerLeave={onHoldEnd}
      disabled={frozen}
      data-testid="arena3-tap-zone"
      data-frozen={frozen ? 'true' : 'false'}
      data-perfect-window={inSweetSpot ? 'true' : 'false'}
      className={`relative flex-1 h-24 rounded-3xl border overflow-hidden bg-gradient-to-br from-emerald-500/15 to-black/60 flex items-center justify-center touch-manipulation select-none disabled:opacity-40 transition-colors ${
        inSweetSpot ? 'border-amber-300/80 shadow-[inset_0_0_20px_-4px_rgba(251,191,36,0.45)]'
                    : 'border-emerald-400/30'
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Charge bar */}
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/60">
        <div className="h-full bg-emerald-400 transition-all duration-150" style={{ width: `${pct}%` }} />
      </div>

      {/* Perfect-Tap cooldown ring (top strip) — highlights sweet spot band */}
      <div className="absolute inset-x-0 top-0 h-1 bg-black/40" aria-hidden>
        {/* sweet-spot band */}
        <div
          className="absolute top-0 h-full bg-amber-300/25"
          style={{ left: `${windowLo}%`, width: `${windowHi - windowLo}%` }}
        />
        {/* moving playhead */}
        <div
          className={`absolute top-0 h-full ${inSweetSpot ? 'bg-amber-300' : 'bg-emerald-300/90'}`}
          style={{ width: '3px', left: `calc(${ringPct}% - 1.5px)` }}
        />
      </div>

      <div className="flex flex-col items-center gap-0.5 pointer-events-none px-2">
        <Zap className={`w-5 h-5 ${inSweetSpot ? 'text-amber-200' : 'text-emerald-300'} ${frozen ? '' : 'animate-pulse'}`} strokeWidth={2} />
        <span className={`text-[10px] font-bold uppercase tracking-widest truncate max-w-[70vw] ${inSweetSpot ? 'text-amber-100' : 'text-emerald-100'}`}>
          {frozen
            ? t('arena.consuming', 'Consuming…')
            : inSweetSpot
              ? t('arena.perfectNow', 'Perfect NOW!')
              : t('arena.tapFast', 'Tap fast')}
        </span>
        {perfectStreak > 1 && !frozen && (
          <span
            className="text-[8px] font-mono uppercase tracking-widest text-amber-200/90"
            data-testid="perfect-streak"
          >
            {perfectStreak}× streak
          </span>
        )}
      </div>

      {/* Perfect flash overlay — bumps every perfect tap */}
      <AnimatePresence>
        {perfectFlashKey > 0 && (
          <motion.div
            key={perfectFlashKey}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.15 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            data-testid="perfect-tap-flash"
          >
            <span className="px-2 py-0.5 rounded-full bg-amber-400/90 text-black text-[10px] font-bold uppercase tracking-widest shadow-[0_0_12px_rgba(251,191,36,0.9)]">
              Perfect tap!
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ripple */}
      <motion.span key={ripple}
        initial={{ scale: 0, opacity: 0.7 }}
        animate={{ scale: 3, opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute w-16 h-16 rounded-full border-2 border-emerald-300 pointer-events-none"
      />
    </button>
  );
}


// -----------------------------------------------------------------------
//  Ultimate Button
// -----------------------------------------------------------------------
function UltimateButton({ charge, fired, onFire, reducedMotion }) {
  const t = useT();
  const pct = Math.min(100, (charge / ULTIMATE_TAP_THRESHOLD) * 100);
  const ready = pct >= 100;
  return (
    <button
      onClick={onFire}
      disabled={!ready || fired}
      data-testid="arena3-ultimate-btn"
      className={`relative rounded-2xl border overflow-hidden flex items-center justify-center transition-all shrink-0
        ${ready && !fired ? 'border-amber-400/70 bg-amber-500/20 shadow-[0_0_20px_-4px_rgba(251,191,36,0.7)]' : 'border-white/10 bg-black/40'}
        ${!ready || fired ? 'opacity-70' : ''}`}
      style={{ width: 'clamp(44px, 12vw, 56px)', height: 'clamp(44px, 12vw, 56px)' }}
    >
      <div className="absolute inset-0" style={{
        background: `conic-gradient(from -90deg, rgba(251,191,36,0.55) ${pct * 3.6}deg, transparent 0deg)`,
      }} />
      <div className="relative flex flex-col items-center">
        <Sparkles className={`w-4 h-4 ${ready ? 'text-amber-300' : 'text-white/50'} ${ready && !reducedMotion ? 'animate-pulse' : ''}`} strokeWidth={2} />
        <span className="text-[8px] uppercase tracking-widest mt-0.5">{fired ? t('arena.ultUsed', 'Used') : ready ? t('arena.ultReady', 'Ult') : `${Math.round(pct)}%`}</span>
      </div>
    </button>
  );
}


// -----------------------------------------------------------------------
//  Ability + Consumable Rows
// -----------------------------------------------------------------------
function AbilityRow({ abilities, onFire }) {
  // Compact inline pill row — one 44×44 button per ability with the name
  // as tooltip only. Fits horizontally with `PrimedConsumables` in the
  // same strip and never grows taller than 44px.
  if (!abilities?.length) return null;
  const items = abilities.slice(0, 4);
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid="arena3-abilities">
      {items.map((a, i) => (
        <button
          key={i}
          onClick={() => a.ready && onFire()}
          disabled={!a.ready}
          data-testid="arena3-ability-btn"
          title={a.name}
          aria-label={a.name}
          className={`h-11 min-w-[44px] px-2 rounded-xl text-[10px] uppercase tracking-widest truncate flex items-center justify-center ${
            a.ready ? 'bg-cyan-400/15 border border-cyan-300/40 text-cyan-100 hover:bg-cyan-400/25 shadow-[0_0_12px_-6px_rgba(103,232,249,0.9)]'
                    : 'bg-white/5 border border-white/10 text-white/40 opacity-60'
          }`}
        >{(a.name || 'Skill').split(' ')[0]}</button>
      ))}
    </div>
  );
}


function PrimedConsumables({ consumables }) {
  if (!consumables?.length) return null;
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid="arena3-consumables">
      {consumables.slice(0, 3).map((c, i) => {
        const meta = effectKindMeta(c);
        return (
          <div
            key={i}
            data-testid="arena3-consumable-primed"
            title={`${c.name || 'Item'} · ${meta.description}`}
            aria-label={`${c.name || 'Item'} primed`}
            className="h-11 min-w-[44px] px-2 rounded-xl text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 border"
            style={{
              background: `linear-gradient(135deg, ${meta.color}22, rgba(0,0,0,0.4))`,
              borderColor: `${meta.color}66`,
              color: '#e5e7eb',
              boxShadow: `0 0 10px -4px ${meta.color}`,
            }}
          >
            <FlaskConical className="w-3.5 h-3.5" style={{ color: meta.color }} />
            <span className="truncate max-w-[52px]">{(c.name || 'Item').split(' ')[0]}</span>
          </div>
        );
      })}
    </div>
  );
}


function ConsumableRow() { return null; }  // deprecated — replaced by PrimedConsumables above


// -----------------------------------------------------------------------
//  VFX overlay layer
// -----------------------------------------------------------------------
function VfxLayer({ flash, slowMo, reducedMotion }) {
  if (reducedMotion) return null;
  const bg = flash === 'crit' ? 'radial-gradient(circle, rgba(251,191,36,0.32), transparent 65%)'
           : flash === 'ultimate' ? 'radial-gradient(circle, rgba(251,191,36,0.55), transparent 70%)'
           : flash === 'combo' ? 'radial-gradient(circle, rgba(217,70,239,0.24), transparent 65%)'
           : flash === 'dodge' ? 'radial-gradient(circle, rgba(103,232,249,0.20), transparent 65%)'
           : null;
  return (
    <>
      {bg && (
        <motion.div
          initial={{ opacity: 0.9 }} animate={{ opacity: 0 }} transition={{ duration: 0.36 }}
          className="absolute inset-0 z-30 pointer-events-none"
          style={{ background: bg }}
        />
      )}
      {slowMo && (
        <div className="absolute inset-0 z-40 pointer-events-none bg-black/25 backdrop-blur-[1px]" />
      )}
    </>
  );
}


// -----------------------------------------------------------------------
//  Living Arena Backdrop (Phase C — CSS/SVG only, no WebGL)
// -----------------------------------------------------------------------
function ArenaBackdrop({ arena, weather, reducedMotion }) {
  // Subtle vignette per biome (only for legibility at top/bottom edges —
  // the photograph itself provides the scene colour now).
  const vignetteByArena = {
    forest:   'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    wetlands: 'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    coast:    'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    mountain: 'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    meadow:   'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    river:    'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    lake:     'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    cave:     'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    tundra:   'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
    desert:   'linear-gradient(180deg, rgba(4,10,8,0.55) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 65%, rgba(4,10,8,0.85) 100%)',
  };
  const vignette = vignetteByArena[arena] || vignetteByArena.forest;

  // Random battlefield image layered as the primary scene. Picked once
  // per battle mount (stable across re-renders) and lazy-loaded via
  // BattlefieldManager. Fades in when the asset decodes so there's no
  // hard cut. Rendered near-opaque so the environment actually reads.
  const [battlefield, setBattlefield] = useState(null);
  const [imageReady, setImageReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const biomeMatchers = {
      forest: 'forest', meadow: 'meadow', wetlands: 'wetlands',
      river: 'river', lake: 'lake', coast: 'coast', mountain: 'mountain',
      tundra: 'tundra', desert: 'desert', grassland: 'meadow',
      swamp: 'wetlands', ocean: 'coast',
    };
    const wantedBiome = biomeMatchers[arena];
    let picked = null;
    if (wantedBiome) picked = getRandomBattlefield({ biome: wantedBiome });
    if (!picked) picked = getRandomBattlefield();
    if (!alive || !picked) return () => { alive = false; };
    setBattlefield(picked);
    preloadSelected(picked).then(() => { if (alive) setImageReady(true); });
    return () => { alive = false; };
  }, [arena]);

  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" data-testid="arena3-backdrop">
      {/* Photo battlefield (near-opaque so the scene actually reads) */}
      {battlefield && (
        <>
          <div
            data-testid="arena3-battlefield-photo"
            data-battlefield-id={battlefield.id}
            data-season={battlefield.season}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${battlefield.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center bottom',
              opacity: imageReady ? 1 : 0,
              transition: 'opacity 700ms ease-out',
              filter: battlefield.grading?.css || 'saturate(1)',
            }}
          />
          {battlefield.grading?.tint && (
            <div
              data-testid="arena3-season-tint"
              className="absolute inset-0"
              style={{ background: battlefield.grading.tint, mixBlendMode: 'soft-light', opacity: 0.6 }}
            />
          )}
        </>
      )}

      {/* Edge vignette — dark at very top (behind HP bars) and bottom
          (behind tap zone) for legibility. Middle stays crystal-clear. */}
      <div className="absolute inset-0" style={{ background: vignette }} />

      {/* Weather particles */}
      {!reducedMotion && weather === 'rain' && <Particles kind="rain" count={40} />}
      {!reducedMotion && weather === 'snow' && <Particles kind="snow" count={30} />}
      {!reducedMotion && weather === 'fog'  && <div className="absolute inset-0 bg-white/[0.04] backdrop-blur-[2px]" />}
      {!reducedMotion && weather === 'storm' && <Particles kind="rain" count={60} intense />}
      {!reducedMotion && (arena === 'forest' || arena === 'meadow') && <Particles kind="firefly" count={12} />}
    </div>
  );
}

function Particles({ kind, count, intense }) {
  const items = useMemo(() => Array.from({ length: count }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    d: 3 + Math.random() * 3,
    delay: Math.random() * 3,
    size: kind === 'firefly' ? 2 + Math.random() * 2 : kind === 'snow' ? 2 + Math.random() * 2 : 1,
  })), [count, kind]);
  const style = kind === 'rain' ? { background: intense ? '#a8d5f0' : '#93b8cf', width: 1, height: 12 }
             : kind === 'snow' ? { background: '#e8ecea', borderRadius: 9999 }
             : { background: '#f2c94c', borderRadius: 9999, boxShadow: '0 0 6px #f2c94c' };
  return (
    <div className="absolute inset-0 pointer-events-none">
      {items.map(p => (
        <span key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: p.size,
          height: (kind === 'rain') ? 12 : p.size,
          ...style,
          animation: `wq-${kind} ${p.d}s linear ${p.delay}s infinite`,
          opacity: kind === 'firefly' ? 0.85 : 0.7,
        }} />
      ))}
    </div>
  );
}


// -----------------------------------------------------------------------
//  Settings sheet
// -----------------------------------------------------------------------
function loadSettings() {
  try {
    return JSON.parse(localStorage.getItem('wq_arena3_settings')) || defaults();
  } catch { return defaults(); }
}
function defaults() {
  return {
    tapAssist: false,
    holdToTap: false,
    reducedMotion: (typeof window !== 'undefined') && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false,
    sound: false,
  };
}

function SettingsSheet({ settings, onClose, onChange, t }) {
  const toggle = (k) => onChange({ ...settings, [k]: !settings[k] });
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="arena3-settings-sheet"
    >
      <motion.div onClick={e => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        className="w-full max-w-sm rounded-3xl bg-[#0e1613] border border-white/10 p-5 space-y-3"
      >
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] text-accent">{t('arena.settings', 'Settings')}</p>
          <button onClick={onClose} className="p-1 rounded-full bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <ToggleRow label={t('arena.tapAssist', 'Tap Assist (auto-taps)')} testid="opt-tap-assist" active={settings.tapAssist} onClick={() => toggle('tapAssist')} />
        <ToggleRow label={t('arena.holdToTap', 'Hold to tap')} testid="opt-hold-to-tap" active={settings.holdToTap} onClick={() => toggle('holdToTap')} />
        <ToggleRow label={t('arena.reducedMotion', 'Reduced motion')} testid="opt-reduced-motion" active={settings.reducedMotion} onClick={() => toggle('reducedMotion')} />
        <ToggleRow label={t('arena.sound', 'Sound (coming soon)')} testid="opt-sound" active={settings.sound} onClick={() => toggle('sound')} icon={settings.sound ? Volume2 : VolumeX} />
        <p className="text-[10px] text-muted-foreground pt-2">
          {t('arena.accessibilityNote', 'Accessibility options do NOT provide competitive advantages — outcome is decided by the hidden combat engine.')}
        </p>
      </motion.div>
    </motion.div>
  );
}
function ToggleRow({ label, active, onClick, testid, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center justify-between p-3 rounded-2xl border ${
        active ? 'border-accent/60 bg-accent/10' : 'border-white/10 bg-white/5'
      }`}
    >
      <span className="text-sm flex items-center gap-2">{Icon && <Icon className="w-4 h-4" />} {label}</span>
      <span className={`inline-block w-8 h-4 rounded-full ${active ? 'bg-accent' : 'bg-white/20'} relative transition-colors`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${active ? 'left-4' : 'left-0.5'}`} />
      </span>
    </button>
  );
}


// -----------------------------------------------------------------------
//  Battle end overlay
// -----------------------------------------------------------------------
function BattleEndOverlay({ winner, chName, opName, roundWins, coinsAwarded, arena, onExit, onRematch, rematching, onShare, t }) {
  const won = winner === 'challenger';
  const label = won ? t('arena.victory', 'Victory') : t('arena.defeat', 'Defeat');
  const winnerName = won ? chName : opName;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="absolute inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-6"
      data-testid="arena3-end"
    >
      <motion.div
        initial={{ scale: 0.86, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-sm space-y-4"
      >
        <div className={`inline-flex items-center gap-2 px-4 py-1 rounded-full ${won ? 'bg-amber-500/15 border border-amber-400/40 text-amber-200' : 'bg-red-500/10 border border-red-400/30 text-red-200'}`}>
          <Award className="w-3.5 h-3.5" />
          <span className="text-[10px] uppercase tracking-[0.3em]">{label}</span>
        </div>
        <h2 className="font-serif text-5xl">{winnerName}</h2>
        <p className="text-xs text-muted-foreground uppercase tracking-widest">{t('arena.takesTheArena', 'takes the arena')}</p>
        <div className="flex items-center justify-center gap-4">
          <span className="font-mono text-2xl text-emerald-300">{roundWins.challenger}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('arena.rounds', 'rounds')}</span>
          <span className="font-mono text-2xl text-red-300">{roundWins.opponent}</span>
        </div>
        {coinsAwarded && (
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-white/10">
            {coinsAwarded.earned ? (
              <span className="font-mono text-sm text-amber-300">+{coinsAwarded.earned}</span>
            ) : null}
            <span className="text-xs">{t('arena.balance', 'Balance:')}</span>
            <span className="font-mono text-lg text-accent">{coinsAwarded.new_balance}</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t('arena.wildcoins', 'wildcoins')}</span>
          </div>
        )}
        <div className="flex flex-col gap-2 pt-1">
          {onRematch && (
            <button onClick={onRematch} data-testid="arena3-end-rematch"
              disabled={rematching}
              className="px-6 py-3 rounded-full bg-accent text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-60 flex items-center justify-center gap-2">
              {rematching ? t('arena.starting', 'Starting…') : t('arena.rematch', 'Rematch')}
            </button>
          )}
          <div className="flex gap-2">
            {won && onShare && (
              <button onClick={onShare} data-testid="arena3-end-share"
                className="flex-1 px-4 py-2.5 rounded-full glass border border-amber-400/40 text-amber-200 font-semibold text-xs uppercase tracking-widest">
                {t('arena.shareWin', 'Share Win')}
              </button>
            )}
            <button onClick={onExit} data-testid="arena3-end-exit"
              className={`${won && onShare ? 'flex-1' : 'w-full'} px-4 py-2.5 rounded-full glass border border-white/10 text-xs uppercase tracking-widest`}>
              {t('arena.continue', 'Continue')}
            </button>
          </div>
        </div>
        {arena && (
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest pt-1">{t('arena.arenaLabel', 'Arena · {arena}', { arena })}</p>
        )}
      </motion.div>
    </motion.div>
  );
}
